import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE, useAuth } from '../App';

const inputStyle = {
  width: '100%', padding: '9px 12px', border: '0.5px solid #AAB0B8',
  borderRadius: '8px', fontSize: '13px', fontFamily: 'Manrope, sans-serif',
  color: '#22272D', background: '#F4FAFF', outline: 'none',
  marginTop: '6px', boxSizing: 'border-box',
};

const labelStyle = {
  fontSize: '13px', color: '#22272D', fontWeight: '500', display: 'block',
};

function formatDateTime(dateStr, timeStr) {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' });
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${weekday}, ${dd}/${mm}/${yy}${timeStr ? ' at ' + formatTime(timeStr) : ''}`;
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12  = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

function isPast(dateStr, timeStr) {
  const now = new Date();
  const dt  = new Date(`${dateStr}T${timeStr || '00:00'}`);
  return dt < now;
}

function StatusBadge({ date, time }) {
  const past = isPast(date, time);
  return (
    <span style={{
      fontSize: '11px', padding: '3px 10px', borderRadius: '20px', fontWeight: '600',
      background: past ? 'rgba(255,118,107,0.15)' : 'rgba(189,255,192,0.3)',
      color:      past ? '#a83232'                : '#1a6b1a',
    }}>
      {past ? 'Completed' : 'Upcoming'}
    </span>
  );
}

// ── Schedule Form Modal ────────────────────────────────────────────────────────
function ScheduleModal({ meetingTypes, users, onClose, onSaved, editing }) {
  const [date,        setDate]        = useState(editing?.date        || '');
  const [time,        setTime]        = useState(editing?.time        || '');
  const [meetingType, setMeetingType] = useState(editing?.meeting_type || (meetingTypes[0]?.name || ''));
  const [location,    setLocation]    = useState(editing?.location    || '');
  const [notes,       setNotes]       = useState(editing?.notes       || '');
  const [attendees,   setAttendees]   = useState(editing?.attendee_ids || []);
  const [sendInvite,  setSendInvite]  = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');

  const toggleAttendee = (id) => {
    setAttendees(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    if (!date)        { setError('Please select a date.');        return; }
    if (!time)        { setError('Please select a time.');        return; }
    if (!meetingType) { setError('Please select a meeting type.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { date, time, meeting_type: meetingType, location, notes, attendee_ids: attendees, send_invite: sendInvite };
      if (editing) {
        await axios.put(`${API_BASE}/api/schedules/${editing.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_BASE}/api/schedules`, payload, { withCredentials: true });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save. Please try again.');
    } finally { setSaving(false); }
  };

  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div onClick={handleBackdrop} style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.55)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div style={{
        background: '#fff', borderRadius: '16px', width: '540px', maxHeight: '90vh',
        overflowY: 'auto', border: '0.5px solid #AAB0B8', padding: '32px',
      }}>
        <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: '18px', fontWeight: '600', color: '#22272D', marginBottom: '24px' }}>
          {editing ? 'Edit Meeting' : 'Schedule a Meeting'}
        </h2>

        {/* Date & Time */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '18px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Time</label>
            <input type="time" value={time} onChange={e => setTime(e.target.value)} style={inputStyle} />
          </div>
        </div>

        {/* Meeting Type */}
        <div style={{ marginBottom: '18px' }}>
          <label style={labelStyle}>Meeting Type</label>
          <select value={meetingType} onChange={e => setMeetingType(e.target.value)} style={inputStyle}>
            {meetingTypes.map(mt => <option key={mt.id} value={mt.name}>{mt.name}</option>)}
          </select>
        </div>

        {/* Location */}
        <div style={{ marginBottom: '18px' }}>
          <label style={labelStyle}>Location</label>
          <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Board Room, Zoom link…" style={inputStyle} />
        </div>

        {/* Notes */}
        <div style={{ marginBottom: '18px' }}>
          <label style={labelStyle}>Notes / Agenda</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Meeting agenda or notes…" style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.5' }} />
        </div>

        {/* Attendees */}
        <div style={{ marginBottom: '18px' }}>
          <label style={labelStyle}>Attendees</label>
          <div style={{ marginTop: '8px', maxHeight: '160px', overflowY: 'auto', border: '0.5px solid #AAB0B8', borderRadius: '8px', padding: '4px 0' }}>
            {users.length === 0 && (
              <div style={{ padding: '12px', fontSize: '13px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>No users found</div>
            )}
            {users.map(u => (
              <label key={u.id} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '8px 14px', cursor: 'pointer',
                background: attendees.includes(u.id) ? 'rgba(241,255,92,0.15)' : 'transparent',
              }}>
                <input
                  type="checkbox"
                  checked={attendees.includes(u.id)}
                  onChange={() => toggleAttendee(u.id)}
                  style={{ accentColor: '#F1FF5C', width: '14px', height: '14px' }}
                />
                <div>
                  <div style={{ fontSize: '13px', color: '#22272D', fontWeight: '500', fontFamily: 'Manrope, sans-serif' }}>{u.name}</div>
                  <div style={{ fontSize: '11px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>{u.email}</div>
                </div>
              </label>
            ))}
          </div>
          <div style={{ fontSize: '11px', color: '#AAB0B8', marginTop: '4px' }}>
            {attendees.length} attendee{attendees.length !== 1 ? 's' : ''} selected
          </div>
        </div>

        {/* Send Invite toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={sendInvite}
            onChange={e => setSendInvite(e.target.checked)}
            style={{ accentColor: '#F1FF5C', width: '14px', height: '14px' }}
          />
          <span style={{ fontSize: '13px', color: '#22272D', fontFamily: 'Manrope, sans-serif' }}>
            Send email invites & reminders to selected attendees
          </span>
        </label>

        {error && (
          <div style={{ padding: '10px 14px', borderRadius: '8px', background: 'rgba(255,118,107,0.15)', color: '#a83232', fontSize: '13px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button type="button" onClick={handleSave} disabled={saving} style={{
            flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
            background: '#F1FF5C', color: '#22272D', fontSize: '14px',
            fontWeight: '600', fontFamily: 'Manrope, sans-serif',
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
          }}>
            {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Schedule Meeting')}
          </button>
          <button type="button" onClick={onClose} disabled={saving} style={{
            flex: 1, padding: '10px', borderRadius: '8px',
            border: '0.5px solid #AAB0B8', background: 'transparent',
            color: '#22272D', fontSize: '14px', fontFamily: 'Manrope, sans-serif',
            cursor: saving ? 'not-allowed' : 'pointer',
          }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Cancel Confirm Modal ──────────────────────────────────────────────────────
function CancelModal({ schedule, onClose, onCancelled }) {
  const [cancelling, setCancelling] = useState(false);
  const [error, setError]           = useState('');

  const handleConfirm = async () => {
    setCancelling(true); setError('');
    try {
      await axios.delete(`${API_BASE}/api/schedules/${schedule.id}`, { withCredentials: true });
      onCancelled();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to cancel meeting.');
      setCancelling(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', width: '420px', border: '0.5px solid #AAB0B8' }}>
        <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: '18px', fontWeight: '600', color: '#22272D', marginBottom: '8px' }}>Cancel Meeting</h2>
        <p style={{ fontSize: '13px', color: '#AAB0B8', marginBottom: '6px' }}>You are about to cancel:</p>
        <p style={{ fontSize: '13px', color: '#22272D', fontWeight: '600', marginBottom: '6px' }}>{schedule.meeting_type}</p>
        <p style={{ fontSize: '13px', color: '#AAB0B8', marginBottom: '20px' }}>{formatDateTime(schedule.date, schedule.time)}</p>
        <p style={{ fontSize: '12px', color: '#FF766B', marginBottom: '20px' }}>Attendees will be notified by email.</p>
        {error && <div style={{ padding: '10px', borderRadius: '8px', background: 'rgba(255,118,107,0.15)', color: '#a83232', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" onClick={handleConfirm} disabled={cancelling} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: '#FF766B', color: '#fff', fontSize: '14px', fontWeight: '600', fontFamily: 'Manrope, sans-serif', cursor: cancelling ? 'not-allowed' : 'pointer', opacity: cancelling ? 0.7 : 1 }}>
            {cancelling ? 'Cancelling…' : 'Yes, Cancel Meeting'}
          </button>
          <button type="button" onClick={onClose} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '0.5px solid #AAB0B8', background: 'transparent', color: '#22272D', fontSize: '14px', fontFamily: 'Manrope, sans-serif', cursor: 'pointer' }}>
            Keep It
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Scheduling Page ──────────────────────────────────────────────────────
function Scheduling() {
  const { user } = useAuth();
  const isSuper = user?.role === 'superadmin';
  const sp = (user?.permissions || {}).scheduling || {};
  const canSchedule = isSuper || !!sp.schedule;   // create new meetings
  const canEdit     = isSuper || !!sp.edit;       // edit existing
  const canCancel   = isSuper || !!sp.cancel;     // cancel existing
  const canManage   = canSchedule || canEdit || canCancel; // can use the page at all

  const [schedules,     setSchedules]     = useState([]);
  const [meetingTypes,  setMeetingTypes]  = useState([]);
  const [users,         setUsers]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showModal,     setShowModal]     = useState(false);
  const [editingItem,   setEditingItem]   = useState(null);
  const [cancelItem,    setCancelItem]    = useState(null);
  const [filter,        setFilter]        = useState('upcoming'); // 'upcoming' | 'all'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [schRes, mtRes, usersRes] = await Promise.all([
        axios.get(`${API_BASE}/api/schedules`, { withCredentials: true }),
        axios.get(`${API_BASE}/api/meeting-types`, { withCredentials: true }),
        canManage ? axios.get(`${API_BASE}/api/users`, { withCredentials: true }) : Promise.resolve({ data: [] }),
      ]);
      setSchedules(schRes.data);
      setMeetingTypes(mtRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error('Failed to load schedules:', err);
    } finally { setLoading(false); }
  }, [canManage]);

  useEffect(() => { load(); }, [load]);

  const filtered = schedules
    .filter(s => filter === 'all' || !isPast(s.date, s.time))
    .sort((a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`));

  const upcoming = schedules.filter(s => !isPast(s.date, s.time));
  const past     = schedules.filter(s =>  isPast(s.date, s.time));

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
        <div>
          <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: '22px', fontWeight: '600', color: '#22272D', margin: 0 }}>
            Scheduling
          </h1>
          <p style={{ fontSize: '13px', color: '#AAB0B8', marginTop: '4px', fontFamily: 'Manrope, sans-serif' }}>
            {upcoming.length} upcoming · {past.length} past
          </p>
        </div>
        {canSchedule && (
          <button type="button" onClick={() => { setEditingItem(null); setShowModal(true); }} style={{
            padding: '10px 22px', borderRadius: '8px', border: 'none',
            background: '#F1FF5C', color: '#22272D', fontSize: '14px',
            fontWeight: '600', fontFamily: 'Manrope, sans-serif', cursor: 'pointer',
          }}>
            + Schedule Meeting
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        {['upcoming', 'all'].map(f => (
          <button key={f} type="button" onClick={() => setFilter(f)} style={{
            padding: '7px 18px', borderRadius: '8px', fontSize: '13px',
            fontFamily: 'Manrope, sans-serif', cursor: 'pointer',
            border: filter === f ? 'none' : '0.5px solid #AAB0B8',
            background: filter === f ? '#22272D' : 'transparent',
            color:      filter === f ? '#F1FF5C' : '#AAB0B8',
            fontWeight: filter === f ? '600' : '400',
          }}>
            {f === 'upcoming' ? 'Upcoming' : 'All Meetings'}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ padding: '60px', textAlign: 'center', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>📅</div>
          <div style={{ fontSize: '14px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>
            {filter === 'upcoming' ? 'No upcoming meetings scheduled.' : 'No meetings yet.'}
          </div>
          {canSchedule && (
            <button type="button" onClick={() => { setEditingItem(null); setShowModal(true); }} style={{
              marginTop: '16px', padding: '10px 22px', borderRadius: '8px', border: 'none',
              background: '#F1FF5C', color: '#22272D', fontSize: '13px',
              fontWeight: '600', fontFamily: 'Manrope, sans-serif', cursor: 'pointer',
            }}>
              Schedule your first meeting
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filtered.map(s => {
            const past = isPast(s.date, s.time);
            return (
              <div key={s.id} style={{
                background: '#fff', border: '0.5px solid #AAB0B8',
                borderRadius: '12px', padding: '20px',
                opacity: past ? 0.75 : 1,
              }}>
                {/* Top row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <span style={{ fontFamily: 'Sora, sans-serif', fontSize: '15px', fontWeight: '600', color: '#22272D' }}>
                        {s.meeting_type}
                      </span>
                      <StatusBadge date={s.date} time={s.time} />
                    </div>
                    <div style={{ fontSize: '13px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>
                      📅 {formatDateTime(s.date, s.time)}
                    </div>
                    {s.location && (
                      <div style={{ fontSize: '13px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif', marginTop: '2px' }}>
                        📍 {s.location}
                      </div>
                    )}
                  </div>

                  {!past && (canEdit || canCancel) && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {canEdit && (
                        <button type="button" onClick={() => { setEditingItem(s); setShowModal(true); }} style={{
                          padding: '6px 14px', borderRadius: '6px', border: 'none',
                          background: '#F1FF5C', color: '#22272D', fontSize: '12px',
                          fontWeight: '600', fontFamily: 'Manrope, sans-serif', cursor: 'pointer',
                        }}>Edit</button>
                      )}
                      {canCancel && (
                        <button type="button" onClick={() => setCancelItem(s)} style={{
                          padding: '6px 14px', borderRadius: '6px', border: 'none',
                          background: '#FF766B', color: '#fff', fontSize: '12px',
                          fontWeight: '600', fontFamily: 'Manrope, sans-serif', cursor: 'pointer',
                        }}>Cancel</button>
                      )}
                    </div>
                  )}
                </div>

                {/* Notes */}
                {s.notes && (
                  <div style={{ fontSize: '13px', color: '#22272D', background: '#F4FAFF', padding: '10px 14px', borderRadius: '8px', marginBottom: '12px', lineHeight: '1.6' }}>
                    {s.notes}
                  </div>
                )}

                {/* Attendees */}
                {s.attendees && s.attendees.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>Attendees:</span>
                    {s.attendees.map(a => (
                      <span key={a.id} style={{
                        fontSize: '11px', padding: '3px 10px', borderRadius: '20px',
                        background: 'rgba(98,240,255,0.2)', color: '#007a8a',
                        fontWeight: '600', fontFamily: 'Manrope, sans-serif',
                      }}>{a.name}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Schedule / Edit Modal */}
      {showModal && (
        <ScheduleModal
          meetingTypes={meetingTypes}
          users={users}
          editing={editingItem}
          onClose={() => { setShowModal(false); setEditingItem(null); }}
          onSaved={load}
        />
      )}

      {/* Cancel Modal */}
      {cancelItem && (
        <CancelModal
          schedule={cancelItem}
          onClose={() => setCancelItem(null)}
          onCancelled={load}
        />
      )}
    </div>
  );
}

export default Scheduling;